/**
 * Module Access Control — Frontend
 *
 * useModuleAccess() — fetches and caches the user's modules + features
 * <ModuleGuard module="talent"> — blocks rendering if module not active
 * <FeatureGuard feature="job_openings"> — blocks rendering if feature not allowed
 */

import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/modules/shared/lib/api-client";
import { useAuth } from "@/modules/shared/hooks/useAuth";

// ─── Types ──────────────────────────────────────────────────────────────────

export type ModuleKey = "shell" | "admin" | "talent" | "sales" | "operations";
export type AccessLevel = "view" | "edit";

interface AccessResponse {
  modules: ModuleKey[];
  features: Record<string, AccessLevel>;
  role: string | null;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const CURRENT_MODULE_STORAGE_KEY = "openhub-current-module";
export { CURRENT_MODULE_STORAGE_KEY };

// ─── Context ────────────────────────────────────────────────────────────────

interface ModuleAccessContextType {
  modules: Set<ModuleKey>;
  features: Record<string, AccessLevel>;
  role: string | null;
  loading: boolean;
  hasModule: (module: ModuleKey) => boolean;
  hasFeature: (feature: string) => boolean;
  canEdit: (feature: string) => boolean;
  refresh: () => Promise<void>;
  currentModule: ModuleKey;
  setCurrentModule: (module: ModuleKey) => void;
  userModules: ModuleKey[];
}

const ModuleAccessContext = createContext<ModuleAccessContextType | undefined>(undefined);

// ─── Provider ──────────────────────────────────────────────────────────────

export function ModuleAccessProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [currentModule, setCurrentModuleState] = useState<ModuleKey>("shell");

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["module-access", user?.id],
    queryFn: async () => {
      if (!user) return null;
      try {
        return await apiFetch<AccessResponse>("/me/access");
      } catch {
        // Fallback: if API fails, allow nothing (fail closed)
        return null;
      }
    },
    enabled: !authLoading && !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  const modules = new Set<ModuleKey>(data?.modules || []);
  const features = data?.features || {};
  const role = data?.role || null;

  // User modules: the list of modules from the API response (excluding shell)
  // User modules: the list of modules from the API response (excluding shell, admin, portal)
  const HIDDEN_MODULES = new Set(["shell", "admin", "portal"]);
  const userModules: ModuleKey[] = data?.modules?.filter((m) => !HIDDEN_MODULES.has(m)) || [];

  // Resolve currentModule from localStorage or default to first available
  useEffect(() => {
    if (isLoading || !data?.modules) return;

    const stored = localStorage.getItem(CURRENT_MODULE_STORAGE_KEY) as ModuleKey | null;

    // If user has only 1 module, auto-select it
    if (userModules.length === 1) {
      setCurrentModuleState(userModules[0]);
      localStorage.setItem(CURRENT_MODULE_STORAGE_KEY, userModules[0]);
      return;
    }

    // If stored module is still available, restore it
    if (stored && userModules.includes(stored)) {
      setCurrentModuleState(stored);
      return;
    }

    // Stored module not available or no stored value — select first module
    if (userModules.length > 0) {
      setCurrentModuleState(userModules[0]);
      localStorage.setItem(CURRENT_MODULE_STORAGE_KEY, userModules[0]);
    } else {
      setCurrentModuleState("shell");
    }
  }, [isLoading, data?.modules]);

  const setCurrentModule = (module: ModuleKey) => {
    setCurrentModuleState(module);
    localStorage.setItem(CURRENT_MODULE_STORAGE_KEY, module);
  };

  // If auth provider is db (legacy), grant all modules
  // This keeps the system working during the migration period
  const isLegacyAuth = import.meta.env.VITE_AUTH_PROVIDER !== "cognito";

  const hasModule = (m: ModuleKey): boolean => {
    if (isLegacyAuth) return true;
    if (!user) return false;
    if (m === "shell") return true; // shell is always available
    return modules.has(m);
  };

  const hasFeature = (f: string): boolean => {
    if (isLegacyAuth) return true;
    if (!user) return false;
    return features[f] === "view" || features[f] === "edit";
  };

  const canEdit = (f: string): boolean => {
    if (isLegacyAuth) return true;
    if (!user) return false;
    return features[f] === "edit";
  };

  const refresh = async () => {
    await refetch();
  };

  return (
    <ModuleAccessContext.Provider
      value={{
        modules,
        features,
        role,
        loading: isLoading,
        hasModule,
        hasFeature,
        canEdit,
        refresh,
        currentModule,
        setCurrentModule,
        userModules,
      }}
    >
      {children}
    </ModuleAccessContext.Provider>
  );
}

export function useModuleAccess() {
  const ctx = useContext(ModuleAccessContext);
  if (!ctx) {
    throw new Error("useModuleAccess must be used within ModuleAccessProvider");
  }
  return ctx;
}

/** Sellable module keys (excludes shell, admin, portal) */
const SELLABLE_MODULES: ModuleKey[] = ["talent", "sales", "operations"];

/**
 * Returns the list of sellable modules the current org does NOT have active.
 * Used by upsell cards and sidebar lock indicators.
 */
export function getInactiveModules(modules: Set<ModuleKey>): ModuleKey[] {
  return SELLABLE_MODULES.filter((m) => !modules.has(m));
}
